export var IFilterSection;
(function (IFilterSection) {
    /**
     * This defines the type of selection for this FilterSection.
     * SINGLE means that 0 or 1 options from the `options` list can be selected.
     * MULTIPLE means that any number of options from the `options` list can be selected.
     * SINGLE_REQUIRED means that exactly 1 option from the `options` list must be selected. The backend must supply a default value in case the frontend doesn't include an option in its request.
     * TOGGLE means that the filter section will have only one option which will be working like a toggle if the option is selected
     * MULTIPLE_OPTIONAL_SELECTIONS means that there will be multiple toggles and the user can select any number of them.
     * NAVIGATION means that the filter section should appear as a tile and disappear when an option is selected. It should behave like a TOGGLE (i.e. only one selection can be made).
     */
    let SelectionType;
    (function (SelectionType) {
        SelectionType["SELECTION_TYPE_UNKNOWN"] = "SELECTION_TYPE_UNKNOWN";
        SelectionType["SINGLE"] = "SINGLE";
        SelectionType["MULTIPLE"] = "MULTIPLE";
        SelectionType["SINGLE_REQUIRED"] = "SINGLE_REQUIRED";
        SelectionType["MULTIPLE_SELECTION_OR_SINGLE_RANGE"] = "MULTIPLE_SELECTION_OR_SINGLE_RANGE";
        SelectionType["TOGGLE"] = "TOGGLE";
        SelectionType["MULTIPLE_OPTIONAL_SELECTIONS"] = "MULTIPLE_OPTIONAL_SELECTIONS";
        SelectionType["NAVIGATION"] = "NAVIGATION";
    })(SelectionType = IFilterSection.SelectionType || (IFilterSection.SelectionType = {}));
    /**
     * This is a useful identifier for all FilterSections, presented in the enum format.
     * Should be used as an id, mapper or determinant for specific behaviour for the frontends.
     * Should not be used as an exhaustive list on the code because more fields may be added in future versions of the
     * backend.
     * Not all FieldNames are returned on all apis, and some field names might be used to identify multiple sections.
     */
    let FieldName;
    (function (FieldName) {
        FieldName["FIELD_NAME_UNKNOWN"] = "FIELD_NAME_UNKNOWN";
        FieldName["CATEGORY"] = "CATEGORY";
        FieldName["WHOLESALE_PRICE"] = "WHOLESALE_PRICE";
        FieldName["MAKER_MINIMUM"] = "MAKER_MINIMUM";
        FieldName["MAKER_VALUE"] = "MAKER_VALUE";
        FieldName["BRAND"] = "BRAND";
        FieldName["INSIDER"] = "INSIDER";
        FieldName["LOCATION"] = "LOCATION";
        FieldName["MAKER_SPECIAL"] = "MAKER_SPECIAL";
        FieldName["LEAD_TIME"] = "LEAD_TIME";
        FieldName["SHIPS_FROM"] = "SHIPS_FROM";
        FieldName["SORTING"] = "SORTING";
        FieldName["CLEARANCE"] = "CLEARANCE";
        FieldName["COLLECTION_PRICE"] = "COLLECTION_PRICE";
        FieldName["RETAIL_PRICE"] = "RETAIL_PRICE";
        FieldName["DELIVERY_METHOD"] = "DELIVERY_METHOD";
        FieldName["PRODUCT_ATTRIBUTE"] = "PRODUCT_ATTRIBUTE";
        FieldName["RETAILER_TYPE"] = "RETAILER_TYPE";
        FieldName["PROXIMITY"] = "PROXIMITY";
        /**
         * used by ConsumerBestseller filters
         */
        FieldName["FOR_PERIOD"] = "FOR_PERIOD";
        /**
         * used by ConsumerBestseller filters
         */
        FieldName["BREAKDOWN"] = "BREAKDOWN";
        /**
         * used by ConsumerBestseller filters
         */
        FieldName["BESTSELLER_CATEGORY"] = "BESTSELLER_CATEGORY";
        /**
         * used by ConsumerBestseller filters
         */
        FieldName["STORE_TYPE"] = "STORE_TYPE";
        /**
         * used by ConsumerBestseller filters
         */
        FieldName["BESTSELLER_MAKER_VALUE"] = "BESTSELLER_MAKER_VALUE";
        /**
         * used by ConsumerBestseller filters
         */
        FieldName["BESTSELLER_REGION"] = "BESTSELLER_REGION";
        /**
         * Flash sale discount filter
         */
        FieldName["DISCOUNT_PERCENTAGE"] = "DISCOUNT_PERCENTAGE";
        /**
         * Immediate vs. preorder filter
         */
        FieldName["DELIVERY_WINDOW"] = "DELIVERY_WINDOW";
        /**
         * hack week 2020 - Eco filters remove after 2021-01-01
         * @deprecated
         */
        FieldName["BRAND_CERTIFICATIONS"] = "BRAND_CERTIFICATIONS";
        /**
         * Used by preorder marketplace
         */
        FieldName["PREORDER_WINDOW"] = "PREORDER_WINDOW";
        /**
         * Used by 2021 Faire Fashion Week
         * @deprecated
         */
        FieldName["FAIRE_FASHION_WEEK"] = "FAIRE_FASHION_WEEK";
        FieldName["TOP_SHOP"] = "TOP_SHOP";
        FieldName["MID_PRICE_POINT_PLUS"] = "MID_PRICE_POINT_PLUS";
        FieldName["ON_MARKET_LIST"] = "ON_MARKET_LIST";
        /**
         * only used for FFW
         * @deprecated
         */
        FieldName["ADDED_TO_CART"] = "ADDED_TO_CART";
        FieldName["PREVIOUSLY_ORDERED"] = "PREVIOUSLY_ORDERED";
        /**
         * only used for FFW
         * @deprecated
         */
        FieldName["FFW_NEVER_ORDERED_DISCOUNT"] = "FFW_NEVER_ORDERED_DISCOUNT";
        FieldName["SHOP_BASED_IN_COUNTRY"] = "SHOP_BASED_IN_COUNTRY";
        FieldName["BRAND_NEW_PRODUCTS"] = "BRAND_NEW_PRODUCTS";
        FieldName["BASED_IN"] = "BASED_IN";
        FieldName["MADE_IN"] = "MADE_IN";
        FieldName["BRAND_PROMOTION"] = "BRAND_PROMOTION";
        FieldName["MARKET_LIST"] = "MARKET_LIST";
        FieldName["EUROPEAN_LOCATION"] = "EUROPEAN_LOCATION";
        FieldName["SHIP_WINDOW"] = "SHIP_WINDOW";
        /**
         * only used for FFW
         * @deprecated
         */
        FieldName["FFW_PREVIOUSLY_ORDERED"] = "FFW_PREVIOUSLY_ORDERED";
        /**
         * @deprecated
         */
        FieldName["FAIRE_SPECIALS"] = "FAIRE_SPECIALS";
        FieldName["USER"] = "USER";
        FieldName["NEW_PRODUCTS"] = "NEW_PRODUCTS";
        FieldName["TAXONOMY_TYPE"] = "TAXONOMY_TYPE";
        FieldName["CART_SUBMITTED_FOR_REVIEW"] = "CART_SUBMITTED_FOR_REVIEW";
        FieldName["FAIRE_PROTECTION"] = "FAIRE_PROTECTION";
        FieldName["BRAND_PROMOTION_TOGGLE"] = "BRAND_PROMOTION_TOGGLE";
        FieldName["LOW_MINIMUM"] = "LOW_MINIMUM";
        /**
         * @deprecated
         */
        FieldName["MARKUP"] = "MARKUP";
        /**
         * Provides granular locations within countries
         */
        FieldName["FAIRE_DETERMINED_LOCATION"] = "FAIRE_DETERMINED_LOCATION";
        FieldName["BRAND_SAMPLES"] = "BRAND_SAMPLES";
        /**
         * Used by Orders page filters
         */
        FieldName["ORDER_DATE"] = "ORDER_DATE";
        /**
         * Used by orders page filters
         */
        FieldName["ORDER_STATUS"] = "ORDER_STATUS";
        /**
         * Used by Eco-friendly product attributes filter
         */
        FieldName["ECO_FRIENDLY_PRODUCT_ATTRIBUTES"] = "ECO_FRIENDLY_PRODUCT_ATTRIBUTES";
        /**
         * Used by Eco-friendly product attribute value quick filter
         */
        FieldName["ECO_FRIENDLY_PRODUCT_ATTRIBUTE_VALUES"] = "ECO_FRIENDLY_PRODUCT_ATTRIBUTE_VALUES";
        FieldName["EXCLUSIVITY"] = "EXCLUSIVITY";
        /**
         * only used for new brands/new arrivals free shipping
         */
        FieldName["NEW_ARRIVALS_FREE_SHIPPING"] = "NEW_ARRIVALS_FREE_SHIPPING";
        FieldName["TRENDING"] = "TRENDING";
        /**
         * Used by Orders page filtering by store.
         */
        FieldName["RETAILER_STORE"] = "RETAILER_STORE";
        /**
         * Used to surface top leaf category pills as toggles.
         */
        FieldName["TOP_CATEGORY_PILLS"] = "TOP_CATEGORY_PILLS";
        /**
         * Used to surface leaf categories as toggles.
         */
        FieldName["CATEGORIES_FOR_YOU"] = "CATEGORIES_FOR_YOU";
        /**
         * Filters products that have at least one option with a GTIN
         */
        FieldName["GLOBAL_TRADE_ITEM_NUMBER"] = "GLOBAL_TRADE_ITEM_NUMBER";
    })(FieldName = IFilterSection.FieldName || (IFilterSection.FieldName = {}));
    /**
     * This defines the backend usage of this FilterSection.
     * Used on the web frontend to separate out sections for filtering from those used for sorting.
     * Should not be used as an exhaustive list on the code because more fields my be added in future versions of the
     * backend.
     */
    let FieldFunction;
    (function (FieldFunction) {
        FieldFunction["FIELD_FUNCTION_UNKNOWN"] = "FIELD_FUNCTION_UNKNOWN";
        FieldFunction["FILTER"] = "FILTER";
        FieldFunction["SORT"] = "SORT";
    })(FieldFunction = IFilterSection.FieldFunction || (IFilterSection.FieldFunction = {}));
    let IFilterBanner;
    (function (IFilterBanner) {
        /**
         * Return an instance of IFilterBanner with the provided fields. Any field not
         * listed will be populated with an empty value (an empty array,
         * or undefined, whichever is valid for the type of the value.)
         */
        IFilterBanner.build = (partial) => (Object.assign({}, (partial !== null && partial !== void 0 ? partial : {})));
    })(IFilterBanner = IFilterSection.IFilterBanner || (IFilterSection.IFilterBanner = {}));
    let IFilterTooltip;
    (function (IFilterTooltip) {
        /**
         * Return an instance of IFilterTooltip with the provided fields. Any field not
         * listed will be populated with an empty value (an empty array,
         * or undefined, whichever is valid for the type of the value.)
         */
        IFilterTooltip.build = (partial) => (Object.assign({}, (partial !== null && partial !== void 0 ? partial : {})));
    })(IFilterTooltip = IFilterSection.IFilterTooltip || (IFilterSection.IFilterTooltip = {}));
    let BadgeType;
    (function (BadgeType) {
        BadgeType["BADGE_TYPE_UNKNOWN"] = "BADGE_TYPE_UNKNOWN";
        BadgeType["NEW_BADGE"] = "NEW_BADGE";
    })(BadgeType = IFilterSection.BadgeType || (IFilterSection.BadgeType = {}));
    let IFilterBadge;
    (function (IFilterBadge) {
        /**
         * Return an instance of IFilterBadge with the provided fields. Any field not
         * listed will be populated with an empty value (an empty array,
         * or undefined, whichever is valid for the type of the value.)
         */
        IFilterBadge.build = (partial) => (Object.assign({}, (partial !== null && partial !== void 0 ? partial : {})));
    })(IFilterBadge = IFilterSection.IFilterBadge || (IFilterSection.IFilterBadge = {}));
    /**
     * Return an instance of IFilterSection with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    IFilterSection.build = (partial) => (Object.assign({ options: [], range_filters: [], option_groups: [] }, (partial !== null && partial !== void 0 ? partial : {})));
})(IFilterSection || (IFilterSection = {}));
